import { useSyncExternalStore } from 'react';

/**
 * Light, dark, or whatever the device is already set to.
 *
 * <p>The design package drives dark mode from `data-theme` alone, which means a device set
 * to dark gets a white screen until someone finds the switch. A phone held in a stockroom at
 * seven in the morning should look like every other app on it, so the default follows the
 * device and the switch is there for the two people who disagree.</p>
 *
 * <p>Resolved here in script rather than in a media query so one attribute on `<html>` is
 * the single answer, and `tokens.css` stays exactly as the package shipped it — that file is
 * the source of truth and is generated alongside its TypeScript twin.</p>
 */
export type Theme = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'norva.theme';

let current: Theme = read();
const listeners = new Set<() => void>();

function read(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    // Blocked storage: follow the device, which is the default anyway.
  }

  return 'system';
}

const prefersDark = () =>
  typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches;

function paint() {
  const resolved = current === 'system' ? (prefersDark() ? 'dark' : 'light') : current;
  document.documentElement.dataset.theme = resolved;
}

export function theme(): Theme {
  return current;
}

export function setTheme(next: Theme) {
  current = next;
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // Not remembering the choice is a smaller problem than not applying it.
  }
  paint();
  for (const listener of listeners) listener();
}

export function onThemeChange(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Called once on start. Keeps following the device while the choice is "system". */
export function startTheme() {
  paint();

  if (typeof matchMedia !== 'function') return;

  matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (current === 'system') paint();
  });
}

/** Re-renders a component when the choice changes, so the switch shows which one is on. */
export function useTheme(): Theme {
  return useSyncExternalStore(onThemeChange, theme, theme);
}
